/**
 * DashboardViewService.ts — Redux thunks for the dashboard canvas/view.
 *
 * Covers:
 *  - retryDomainThunk          — single-domain retry after failure banner
 *  - resetDashboardThunk       — full or partial reset (confirmation-gated from UI)
 *  - syncDashboardThunk        — incremental sync (Sync button)
 *  - loadDashboardDataV1Thunk  — batch-fetch per-panel display data after sync/build
 *  - buildDashboardThunk       — builds dashboard via POST /v1/dashboards/:id/build
 *
 * Spec: §3d.9
 */
import type { AppDispatch, RootState } from '@/redux'
import { DashboardsAPI } from '@/API/Dashboards'
import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { renameDashboardCreationThunk } from '@/services/dashboardsService'
import { toast } from '@/redux/reducers/toast'
import { Toast, ToastVariant } from '@/constants/toast'

// ─── Auto-name helpers ────────────────────────────────────────────────────────

/**
 * Derives a human-readable dashboard name from a list of unqualified picker
 * keys and a year.
 *
 * - Single domain:  "Enrollment 2026"
 * - Multi-domain:   "Enrollment + Bunks 2026"
 *
 * Title-cases the picker key directly. The four v1 picker keys
 * (`enrollment`, `financial`, `bunks`, `staff`) all happen to title-case to
 * their human-readable display names, so the prior display-name lookup map
 * was a no-op identity lookup. Slice 4 of EPIC-132's metadata refactor (TDD
 * §7.11) deletes that map; service code that needs a registry-aware
 * display-name resolution (e.g. for qualified ids) goes through
 * `selectDomainDisplayNameByQualifiedId` in the registry slice.
 */
export const buildAutoName = (domainIds: string[], year: number): string => {
  const labels = domainIds.map(
    id => id.charAt(0).toUpperCase() + id.slice(1),
  )
  return `${labels.join(' + ')} ${year}`
}

/**
 * Returns a name that is unique within `existingNames`.
 *
 * Uniqueness is case-insensitive and trims surrounding whitespace.
 * If `base` is already unique it is returned as-is.
 * Otherwise appends " (2)", " (3)" … until a unique candidate is found.
 */
export const makeUniqueName = (base: string, existingNames: string[]): string => {
  const normalised = existingNames.map(n => n.trim().toLowerCase())
  if (!normalised.includes(base.trim().toLowerCase())) return base

  let suffix = 2
  while (normalised.includes(`${base.trim().toLowerCase()} (${suffix})`)) {
    suffix++
  }
  return `${base} (${suffix})`
}

// ─── retryDomainThunk ─────────────────────────────────────────────────────────

/**
 * Re-runs DomainOrchestratorService.fetchOne for a single failed domain.
 * Kicks off the retry and returns immediately. Socket.IO drives the status
 * updates (sync:domain:complete or sync:domain:error).
 */
export const retryDomainThunk =
  (token: string, dashboardId: string, domainId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(dashboardViewActions.startDomainFetch(domainId))

    const res = await DashboardsAPI.retryDomain(token, dashboardId, domainId)

    if (res.err) {
      // Backend returned an error before the domain fetch even started.
      // Show an error message on the banner. Real-time errors arrive via Socket.IO.
      dispatch(
        dashboardViewActions.setDomainError({
          domainId,
          errorCode: 'RETRY_REQUEST_FAILED',
          errorUserMessage: res.err,
        })
      )
    }
    // On success: Socket.IO sync:domain:complete / sync:domain:error drives status.
  }

// ─── Sync-start helper: surface "Syncing…" chip without waiting on socket ────

/**
 * Returns the unique set of domainIds referenced by the dashboard's panels
 * (read from `state.dashboardPanels.panels[].config.dataSources[].domainId`).
 *
 * Why: chip-honesty fix (CEO directive 2026-05-29). LastSyncChip derives the
 * `syncing` state from `state.dashboardView.domainStatus[*].status === 'fetching'`.
 * Without this helper, on Sync/Reset click the FE only flipped the top-level
 * `isSyncing` flag — the per-domain `fetching` status was set by socket events
 * (`sync:domain:progress`), which arrive AFTER the BE orchestrator entry. With
 * a multi-minute sync that means the chip lied with "Not yet synced" for the
 * whole sync window.
 *
 * Now both `syncDashboardThunk` and `resetDashboardThunk` dispatch
 * `startDomainFetch(domainId)` for every known domain BEFORE the API call,
 * so the chip flips to "Syncing…" within milliseconds and stays there until
 * the BE socket events arrive with per-domain success/error.
 *
 * Returns `[]` if the dashboard's panels haven't been hydrated yet — the
 * subsequent socket events will still drive the chip state correctly in
 * that case (the optimistic path is a no-op).
 */
function extractDashboardDomainIds(state: RootState): string[] {
  const panels = state.dashboardPanels.panels
  const ids = new Set<string>()
  for (const panel of panels) {
    // `DashboardPanelV1.domainId` is the qualified `<appCode>.<entity>-dashboard`
    // id, populated server-side from the panel's primary data source. This is
    // the same field PanelCanvasV1 reads to derive isCached (PanelCanvasV1.tsx
    // ~L384). `DataSourceConfig` does NOT declare a domainId field, so reading
    // from config.dataSources[] would silently no-op in production.
    const did = (panel as { domainId?: unknown }).domainId
    if (typeof did === 'string' && did.length > 0) {
      ids.add(did)
    }
  }
  return Array.from(ids)
}

// ─── resetDashboardThunk ──────────────────────────────────────────────────────

/**
 * Wipes cached data and re-fetches everything from CampMinder.
 * Optionally scoped to specific domainIds (for domain-level reset from error banner).
 * Confirmation-gated from the UI — component must show AlertDialog before dispatching.
 */
export const resetDashboardThunk =
  (token: string, dashboardId: string, domainIds?: string[]) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(dashboardViewActions.setSyncing(true))

    // Chip-honesty (CEO 2026-05-29): optimistically mark every known domain
    // as `fetching` so LastSyncChip shows "Syncing…" immediately, without
    // waiting on the BE socket (`sync:domain:progress`), which can arrive
    // many seconds after the API call returns on a long fan-out sync.
    const knownDomainIds = (domainIds && domainIds.length > 0)
      ? domainIds
      : extractDashboardDomainIds(getState())
    for (const did of knownDomainIds) {
      dispatch(dashboardViewActions.startDomainFetch(did))
    }

    const res = await DashboardsAPI.resetDashboard(token, dashboardId, domainIds ? { domainIds } : undefined)

    if (res.err) {
      // Reset request failed. Syncing flag cleared; banner will show via Socket.IO.
      dispatch(dashboardViewActions.setSyncing(false))
      return
    }

    // Socket.IO sync:dashboard:complete will call markDashboardComplete.
    // isSyncing remains true until that event arrives.
  }

// ─── syncDashboardThunk ───────────────────────────────────────────────────────

/**
 * Default incremental sync (target of the Sync button).
 * Uses CampMinder's lastupdated filter with primary-key merge.
 */
export const syncDashboardThunk =
  (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    dispatch(dashboardViewActions.setSyncing(true))

    // Chip-honesty (CEO 2026-05-29): same optimistic startDomainFetch as
    // resetDashboardThunk. See extractDashboardDomainIds docstring above
    // for why this matters.
    for (const did of extractDashboardDomainIds(getState())) {
      dispatch(dashboardViewActions.startDomainFetch(did))
    }

    // Multi-banner stacking rule (§34.3, CEO 2026-05-05):
    // At sync start, clear the domain error stack so stale errors from the prior
    // attempt do not persist into this one. Do NOT clear authExpiredEnvelope or
    // rateLimitedUntil — those represent the user's still-active credential/rate
    // state and must persist until explicitly resolved.
    dispatch(dashboardViewActions.clearDomainErrors())

    const res = await DashboardsAPI.syncDashboard(token, dashboardId)

    if (res.err) {
      // 401 — credential revoked/expired. Routes to authExpiredEnvelope slot.
      const is401 = res.err.includes('401') || res.err.toLowerCase().includes('unauthorized')
      if (is401) {
        dispatch(dashboardViewActions.setDashboardError({
          code: 'AUTH_EXPIRED',
          message: 'Your CampMinder connection expired. Reconnect to keep syncing.',
          vendor: 'campminder',
        }))
      }
      dispatch(dashboardViewActions.setSyncing(false))
      return
    }

    // Socket.IO sync:dashboard:complete will call markDashboardComplete.
    // On success for vendor campminder: clear authExpiredEnvelope if it was set
    // for this vendor — the credential issue has been resolved.
    dispatch(dashboardViewActions.clearAuthExpired())
  }

// ─── loadDashboardDataV1Thunk ─────────────────────────────────────────────────

// Module-level serial counter — ensures only the most recent in-flight
// loadDashboardDataV1Thunk call commits its result to Redux. Concurrent/overlapping
// calls (e.g. rapid socket events that bounce v1IsSyncing) increment this counter;
// older calls detect they are stale via the `mySerial !== panelDataV1LoadSerial`
// check in the finally block and discard their response.
let panelDataV1LoadSerial = 0

/**
 * Resets the module-level serial counter to 0.
 *
 * Called in two contexts:
 *  1. **Production navigation** — Canvas.tsx Effect 1 calls this on dashboard-id
 *     transition so any in-flight loadDashboardDataV1Thunk for the previous
 *     dashboard is immediately invalidated (bugbash 2026-06-01 symptoms 2 + 3).
 *  2. **Test isolation** — tests call this between runs to prevent serial state
 *     leaking across test cases.
 *
 * Internal to the DashboardViewService module; exported only for Canvas and tests.
 */
export function _resetPanelDataV1LoadSerial(): void {
  panelDataV1LoadSerial = 0
}

/**
 * Fetches per-panel display data from GET /v1/dashboards/:id/data and stores
 * it in dashboardViewSlice.panelDataMap.
 *
 * Called:
 *  - After `buildDashboardThunk` marks the build complete (PR 4).
 *  - After `markDashboardComplete` fires via Socket.IO (incremental/reset sync).
 *  - On first mount of PanelCanvasV1 when panelDataMap is empty (hydration).
 *
 * Uses a serial counter (panelDataV1LoadSerial) to discard stale responses when
 * multiple calls are dispatched concurrently. Only the most recently dispatched
 * call commits its result to Redux — older in-flight calls are silently dropped.
 * Mirrors the serial-counter pattern in DashboardSyncService.loadDashboardData.
 */
export const loadDashboardDataV1Thunk =
  (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const mySerial = ++panelDataV1LoadSerial

    const res = await DashboardsAPI.getDashboardDataV1(token, dashboardId)

    // Discard if a newer call was dispatched while this one was in-flight.
    if (mySerial !== panelDataV1LoadSerial) return

    // Stale-dashboard guard: if the user navigated away during the fetch, discard.
    const currentId = getState().dashboards.selectedDashboard?.id
    if (currentId !== dashboardId) return

    if (res.err || !res.data) {
      // 404 is expected when the dashboard exists but has never been built/synced.
      // Silently skip so panels show their individual "no data yet" state.
      // All other 4xx/5xx errors are unexpected — surface via toast.
      if (res.status !== 404) {
        toast({
          title: Toast.ERROR,
          variant: ToastVariant.ERROR,
          description: "Couldn't load dashboard data. Please try again.",
        })
      }
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[loadDashboardDataV1Thunk] skipped:', res.err ?? 'no data', 'status:', res.status)
      }
      return
    }

    dispatch(dashboardViewActions.setPanelDataMap(res.data.panels))

    // Chip-hydration — restore "Synced X ago" state after page refresh.
    //
    // `state.dashboardView.domainStatus` is normally populated by live socket
    // events (`sync:domain:complete`). Those events don't replay on refresh,
    // leaving `domainStatus` empty and causing the chip to show "Not yet synced"
    // even when the domain has been synced before.
    //
    // PRIMARY PATH — domainSyncStatus (schema-immune, bugbash 2026-06-01):
    //   The BE now returns `domainSyncStatus: Array<{ domainId, lastSyncedAt }>`
    //   on GET /v1/dashboards/:id/data. This array is written from the sync
    //   checkpoint table, independent of panel schema-version state. When an SDK
    //   bump causes `panels[*].lastSyncedAt` to be discarded with the panel rows,
    //   `domainSyncStatus` is preserved — so the chip correctly shows the prior
    //   sync time instead of "Not yet synced".
    //
    // FALLBACK PATH — per-panel lastSyncedAt (legacy BE compatibility):
    //   When `domainSyncStatus` is absent (older BE version), derive the domain
    //   timestamp by scanning `panels[*].lastSyncedAt` keyed by the panel →
    //   domain mapping in dashboardPanels slice. This was the sole path before
    //   this fix; it remains as a backstop so nothing regresses on older BE.
    if (res.data.domainSyncStatus && res.data.domainSyncStatus.length > 0) {
      // Primary path: authoritative checkpoint timestamps from the BE.
      //
      // BE contract: at most one entry per domainId (checkpoint table uses
      // domainId as primary key). No client-side dedup needed, but the loop
      // short-circuits on null lastSyncedAt (never-synced domain).
      for (const entry of res.data.domainSyncStatus) {
        if (!entry.lastSyncedAt) continue
        // Do not overwrite an active error state — the socket event that set
        // setDomainError carries fresher information about the most recent
        // sync attempt than the checkpoint timestamp. Hydration should restore
        // "Synced X ago" on page refresh, not silently clear a live error.
        const existingStatus = getState().dashboardView?.domainStatus?.[entry.domainId]
        if (existingStatus?.status === 'error') continue
        dispatch(dashboardViewActions.markDomainComplete({
          domainId: entry.domainId,
          warnings: [],
          syncMode: 'incremental',
          syncedAt: entry.lastSyncedAt,
        }))
      }
    } else {
      // Fallback path: per-panel derivation for older BE versions that don't
      // emit domainSyncStatus. Schema-fragile but backward-compatible.
      // Remove this branch once all BE deployments are on the post-bugbash
      // 2026-06-01 version that emits domainSyncStatus.
      const panelDefs = getState().dashboardPanels.panels
      const latestSyncByDomain = new Map<string, string>()
      for (const panelData of res.data.panels) {
        // Hydrate domainStatus for any panel that has a real lastSyncedAt, regardless
        // of current status — a domain that previously succeeded (has lastSyncedAt)
        // should show "Synced X ago", even if the most recent attempt errored.
        if (!panelData.lastSyncedAt) continue
        const def = panelDefs.find(p => p.id === panelData.panelId)
        const domainId = (def as { domainId?: unknown } | undefined)?.domainId
        if (typeof domainId !== 'string' || domainId.length === 0) continue
        const prev = latestSyncByDomain.get(domainId)
        // Keep the MAX lastSyncedAt across panels of the same domain.
        if (!prev || panelData.lastSyncedAt > prev) {
          latestSyncByDomain.set(domainId, panelData.lastSyncedAt)
        }
      }
      for (const [domainId, syncedAt] of latestSyncByDomain) {
        // Same error-state guard as primary path: don't overwrite a live
        // sync:domain:error with a stale checkpoint timestamp.
        const existingStatus = getState().dashboardView?.domainStatus?.[domainId]
        if (existingStatus?.status === 'error') continue
        dispatch(dashboardViewActions.markDomainComplete({
          domainId,
          warnings: [],
          syncMode: 'incremental',
          syncedAt,
        }))
      }
    }
  }

// ─── buildDashboardThunk ──────────────────────────────────────────────────────

/**
 * Builds the dashboard via POST /v1/dashboards/:id/build.
 *
 * The new deterministic build endpoint materialises panel `config` server-side
 * from `PanelProposalHint.suggestedTransform` + `flow_context`. The FE only
 * sends `confirmedPanelIds` — no fully-materialised config needed.
 *
 * On success: dispatches `setFlowState('complete')` as an optimistic update so
 * the UI does not appear stuck if socket delivery is delayed. The BE also emits
 * `sync:creation:complete` on the `dashboard:<id>` socket room, which the Canvas
 * socket listener handles for final navigation + downstream sync.
 *
 * After advancing the FSM to 'complete', `syncDashboardThunk` is dispatched as
 * fire-and-forget. Rationale: `sync:creation:complete` is a semantic socket
 * event — it signals "panels were persisted" but does NOT start a data sync.
 * Nothing else calls `POST /v1/dashboards/:id/sync` after build, so without
 * this dispatch every panel would stay in the "No data yet" state until the
 * user manually pressed Sync. The sync runs concurrently after FSM advance so
 * the canvas renders immediately; the `DomainProgressBanner` (isSyncing=true)
 * shows during the fetch, and `sync:dashboard:complete` clears it when done.
 *
 * Error codes handled:
 *   409 INVALID_FSM_TRANSITION   → dispatch setBuildStatus('fsm_conflict')
 *   400 UNSUPPORTED_DOMAIN       → dispatch setBuildStatus('unsupported_domain') + setBuildErrorDomainId
 *   400 MISSING_CREDENTIAL       → dispatch setBuildStatus('missing_credential')
 *   400 UNKNOWN_PANEL_ID         → dispatch setBuildStatus('unknown_panel')
 *   400 NO_PANELS_CONFIRMED      → guard at call site (confirmedPanelIds.length === 0)
 *   5xx                          → dispatch setBuildStatus('error')
 *
 * @param token        - JWT for the authenticated user.
 * @param dashboardId  - UUID of the dashboard being built.
 */
export const buildDashboardThunk =
  (token: string, dashboardId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const { confirmedPanelIds } = getState().dashboardCreation

    if (confirmedPanelIds.length === 0) {
      // Guard: nothing selected — no-op.
      return
    }

    dispatch(dashboardCreationActions.setBuildStatus('loading'))
    dispatch(dashboardCreationActions.setBuildErrorDomainId(null))

    const idempotencyKey =
      globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)

    const buildResp = await DashboardsAPI.buildDashboard(token, dashboardId, {
      confirmedPanelIds,
      idempotencyKey,
    })

    if (buildResp.err || !buildResp.data) {
      const code = buildResp.errorCode

      if (code === 'INVALID_FSM_TRANSITION') {
        // Bug #4 (2026-05-14): plumb the BE's actualState so BuildErrorWell can
        // render step-specific copy. Without this the FE unconditionally said
        // "Dashboard already built" for every 409 — including the much more
        // common case where the user clicked Build while the BE was still at
        // awaiting_filters or earlier.
        dispatch(
          dashboardCreationActions.setBuildErrorActualState(
            buildResp.errorActualState ?? null,
          ),
        )
        dispatch(dashboardCreationActions.setBuildStatus('fsm_conflict'))
        return
      }

      if (code === 'UNSUPPORTED_DOMAIN') {
        // The BE returns the failing domainId in the error body alongside the error code.
        // `buildResp.errorDomainId` surfaces it so we can render a targeted message.
        dispatch(dashboardCreationActions.setBuildStatus('unsupported_domain'))
        dispatch(dashboardCreationActions.setBuildErrorDomainId(buildResp.errorDomainId ?? null))
        return
      }

      if (code === 'MISSING_CREDENTIAL') {
        dispatch(dashboardCreationActions.setBuildStatus('missing_credential'))
        return
      }

      if (code === 'UNKNOWN_PANEL_ID') {
        dispatch(dashboardCreationActions.setBuildStatus('unknown_panel'))
        return
      }

      // 5xx or unknown error
      dispatch(dashboardCreationActions.setBuildStatus('error'))
      return
    }

    // ── Auto-name: only when the user has NOT manually edited the name ───────
    // If the name is still the default 'Untitled Dashboard' and the user never
    // touched the inline title bar, derive a deterministic name from the
    // selected domain ids + current year, then dispatch the creation rename
    // thunk (optimistic update + PATCH /v1/dashboards/:id).
    const creationState = getState().dashboardCreation
    const allDashboards = getState().dashboards.dashboards

    if (
      !creationState.nameManuallyEdited &&
      creationState.dashboardName === 'Untitled Dashboard' &&
      creationState.selectedDomainIds.length > 0
    ) {
      const year = new Date().getFullYear()
      const baseName = buildAutoName(creationState.selectedDomainIds, year)
      const existingNames = allDashboards.map(d => d.name)
      const uniqueName = makeUniqueName(baseName, existingNames)

      // renameDashboardCreationThunk: optimistic update in dashboardCreationSlice
      // + PATCH /v1/dashboards/:id. On PATCH failure it rolls back to previousName.
      await (dispatch as (action: unknown) => Promise<unknown>)(
        renameDashboardCreationThunk(token, dashboardId, uniqueName, 'Untitled Dashboard')
      )
    }

    // Optimistic: advance creation FSM locally so the UI does not stall if
    // the `sync:creation:complete` socket event is delayed. FSM advances FIRST
    // so the canvas renders before the sync fetch begins.
    dispatch(dashboardCreationActions.setFlowState('complete'))
    dispatch(dashboardCreationActions.setBuildStatus('complete'))

    // Fire-and-forget: trigger the post-build data sync. `sync:creation:complete`
    // is semantic ("panels persisted"), not active — it does NOT call POST /sync.
    // Without this dispatch panels show "No data yet" until the user manually
    // syncs. The banner (DomainProgressBanner via isSyncing) appears while the
    // fetch runs; `sync:dashboard:complete` socket clears it when done.
    void (dispatch as (action: unknown) => unknown)(
      syncDashboardThunk(token, dashboardId)
    )
  }
